/**
 * Staff Allocation Watcher
 *
 * Monitors changes to the Staff Allocation card and syncs them with the Job Tallies system
 */

import {jobTalliesManager} from './job-tallies-refactored';
import Ajax from './ajax';

class StaffAllocationWatcher {
	constructor() {
		this.staffCard = null;
		this.observer = null;
		this.roleMapping = {
			'Shearing': 'shearing_crutching',
			'Shearers': 'shearing_crutching',
			'Crutcher': 'shearing_crutching',
			'Woolhandler': 'woolhandler',
			'Classer': 'classer',
			'Contract Classer': 'classer',
			'Cross Bred Classer': 'classer',
			'Presser': 'presser',
			'Sheepo': 'sheepo'
			// Note: Team Leader is intentionally excluded - it's an administrative role, not a tally role
		};
		this.currentStaffData = new Map(); // Track current staff allocations
	}

	/**
	 * Initialize the watcher
	 */
	init() {
		this.staffCard = document.getElementById('staff');
		if (!this.staffCard) {
			console.warn('Staff allocation card not found.');
			return;
		}

		// Initial scan of current staff
		this.scanCurrentStaff();

		// Set up mutation observer to watch for changes
		this.setupObserver();

		// Listen for Ajax staff added events
		this.setupAjaxListeners();

		console.log('Staff Allocation Watcher initialized');
	}

	/**
	 * Set up Ajax event listeners
	 */
	setupAjaxListeners() {
		// Listen for staff added events from Ajax
		Ajax.on('staffAdded', (data) => {
			console.log('Ajax staffAdded event received:', data);
			// Trigger a refresh after a short delay to allow DOM updates
			setTimeout(() => {
				this.refresh();
			}, 300);
		});
	}

	/**
	 * Scan current staff allocations and populate initial data
	 */
	scanCurrentStaff() {
		const allocatedStaffSections = this.staffCard.querySelectorAll('.allocated-staff');
		console.log('Scanning current staff allocations...');

		allocatedStaffSections.forEach(section => {
			const roleElement = section.querySelector('.role h4');
			if (!roleElement) return;

			const roleName = roleElement.textContent.trim();
			const mappedRoleId = this.roleMapping[roleName];

			if (!mappedRoleId) return; // Skip unmapped roles

			// Get staff rows for this role - include all rows with data
			const staffRows = section.querySelectorAll('tbody tr');
			const staffList = [];

			staffRows.forEach(row => {
				const refCell = row.querySelector('td:first-child');
				const nameCell = row.querySelector('td:nth-child(2)');

				// Check if this is a valid staff row (has ref number and name)
				if (refCell && nameCell) {
					const staffId = refCell.textContent.trim();
					const staffName = nameCell.textContent.trim();
					
					// Only add if we have both ID and name
					if (staffId && staffName && staffId !== '' && staffName !== '') {
						console.log(`Initial scan - Found staff: ${staffName} (${staffId}) in role ${roleName}`);
						staffList.push({
							staffId: staffId,
							staffName: staffName,
							roleId: mappedRoleId,
							roleName: roleName,
							originalRole: roleName // Store original role name for section mapping
						});
					}
				}
			});

			console.log(`Initial scan - Role ${roleName}: found ${staffList.length} staff`);
			// Store current staff for this role
			this.currentStaffData.set(roleName, staffList);

			// Add staff to tallies
			staffList.forEach(staff => {
				jobTalliesManager.addStaffToRole(staff.staffId, staff.staffName, staff.roleId, staff.originalRole);
			});
		});
	}

	/**
	 * Set up mutation observer to watch for DOM changes
	 */
	setupObserver() {
		if (this.observer) {
			this.observer.disconnect();
		}

		this.observer = new MutationObserver((mutations) => {
			let staffChanged = false;

			mutations.forEach((mutation) => {
				// Check if changes occurred in staff allocation tables
				if (mutation.type === 'childList') {
					const target = mutation.target;

					// Check if change is in a staff table or related container
					if (target.closest('.allocated-staff__table') || 
						target.closest('.allocated-staff') || 
						target.classList.contains('allocated-staff') ||
						target.querySelector('.allocated-staff__table')) {
						staffChanged = true;
						console.log('Staff allocation DOM change detected:', mutation);
					}
				}
				
				// Also check for attribute changes that might affect staff display
				if (mutation.type === 'attributes' && mutation.target.closest('.allocated-staff')) {
					staffChanged = true;
					console.log('Staff allocation attribute change detected:', mutation);
				}
			});

			if (staffChanged) {
				// Debounce rapid changes
				clearTimeout(this.debounceTimeout);
				this.debounceTimeout = setTimeout(() => {
					this.handleStaffChange();
				}, 200); // Increased timeout slightly for more stability
			}
		});

		// Start observing with more comprehensive options
		this.observer.observe(this.staffCard, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['class', 'style']
		});
	}

	/**
	 * Handle staff allocation changes
	 */
	handleStaffChange() {
		console.log('Staff allocation change detected');

		const allocatedStaffSections = this.staffCard.querySelectorAll('.allocated-staff');
		const newStaffData = new Map();

		// Scan current state
		allocatedStaffSections.forEach(section => {
			const roleElement = section.querySelector('.role h4');
			if (!roleElement) return;

			const roleName = roleElement.textContent.trim();
			const mappedRoleId = this.roleMapping[roleName];

			if (!mappedRoleId) return; // Skip unmapped roles

			// Get current staff rows for this role - include all rows with data
			const staffRows = section.querySelectorAll('tbody tr');
			const currentStaffList = [];

			staffRows.forEach(row => {
				const refCell = row.querySelector('td:first-child');
				const nameCell = row.querySelector('td:nth-child(2)');

				// Check if this is a valid staff row (has ref number and name)
				if (refCell && nameCell) {
					const staffId = refCell.textContent.trim();
					const staffName = nameCell.textContent.trim();
					
					// Only add if we have both ID and name (and not header row text)
					if (staffId && staffName && staffId !== '' && staffName !== '' 
						&& staffId !== 'Ref Number' && staffName !== 'Staff Name') {
						console.log(`Found staff: ${staffName} (${staffId}) in role ${roleName}`);
						currentStaffList.push({
							staffId: staffId,
							staffName: staffName,
							roleId: mappedRoleId,
							roleName: roleName,
							originalRole: roleName // Store original role name for section mapping
						});
					}
				}
			});

			console.log(`Role ${roleName}: found ${currentStaffList.length} staff`);
			newStaffData.set(roleName, currentStaffList);
		});

		// Compare with previous state and sync changes BEFORE updating stored data
		this.syncStaffChanges(newStaffData);

		// Update stored data AFTER syncing
		this.currentStaffData = newStaffData;
	}

	/**
	 * Sync staff changes with job tallies
	 */
	syncStaffChanges(newStaffData) {
		// Check each role for changes
		for (const [roleName, newStaffList] of newStaffData) {
			const previousStaffList = this.currentStaffData.get(roleName) || [];
			const mappedRoleId = this.roleMapping[roleName];

			if (!mappedRoleId) continue;

			// Find added staff
			const addedStaff = newStaffList.filter(newStaff =>
				!previousStaffList.some(prevStaff => prevStaff.staffId === newStaff.staffId)
			);

			// Find removed staff
			const removedStaff = previousStaffList.filter(prevStaff =>
				!newStaffList.some(newStaff => newStaff.staffId === prevStaff.staffId)
			);

			// Add new staff to tallies
			addedStaff.forEach(staff => {
				console.log(`Adding staff ${staff.staffName} (${staff.staffId}) to role ${mappedRoleId}`);
				jobTalliesManager.addStaffToRole(staff.staffId, staff.staffName, mappedRoleId, staff.originalRole);
			});

			// Remove staff from tallies
			removedStaff.forEach(staff => {
				console.log(`Removing staff ${staff.staffName} (${staff.staffId}) from role ${mappedRoleId} (originalRole: ${staff.originalRole})`);
				this.removeStaffFromRole(staff.staffId, mappedRoleId, staff.originalRole);
			});
		}
	}

	/**
	 * Handle staff removal from job allocation
	 * We retain tally data for removed staff - they remain visible in tallies
	 * but are marked as removed so they display differently
	 */
	removeStaffFromRole(staffId, roleId, originalRole = null) {
		if (!jobTalliesManager.talliesData) return;

		jobTalliesManager.talliesData.tally_data.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === roleId);
			if (!roleData) return;

			// Mark staff as removed but retain their tally data
			roleData.staff_counts.forEach(staff => {
				if (staff.staff_id === staffId && staff.originalRole === originalRole) {
					staff.removed = true;
				}
			});
		});

		// Re-render navigation to show/hide tabs based on staff allocation
		jobTalliesManager.renderRoleNavigation();

		// Re-render if this is the current role
		if (jobTalliesManager.currentRoleId === roleId) {
			jobTalliesManager.renderTablesForRole(roleId);
		}

		// Always re-render all summary tables after removing staff
		jobTalliesManager.renderAllSummaryTables();

		// Sync to hidden input
		jobTalliesManager.syncToHiddenInput();
	}

	/**
	 * Manually refresh staff allocation data
	 * This can be called externally when dynamic changes occur
	 */
	refresh() {
		console.log('Manually refreshing staff allocation data');
		this.handleStaffChange();
	}

	/**
	 * Reset and rescan all staff allocation data
	 */
	reset() {
		console.log('Resetting staff allocation data');
		this.currentStaffData.clear();
		this.scanCurrentStaff();
	}

	/**
	 * Destroy the watcher
	 */
	destroy() {
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
		clearTimeout(this.debounceTimeout);
	}
}

// Initialize the watcher
const staffAllocationWatcher = new StaffAllocationWatcher();

export default staffAllocationWatcher;